import { formatNumber } from '@angular/common';
import { DateTime } from 'luxon';
import { SupportedReconProvider } from '@app/shared/constants';
import {
  CourierInvoiceAuditDetail,
  InvoiceAuditSectionName,
  InvoiceAuditSectionState,
} from '@app/pages/authenticated/pages/finance/finance.service';

/**
 * The one place the Courier Invoices table and the Courier Invoice detail page read a
 * recon_audits row's outcome, Retry and wording rules from. Pure functions only.
 */

// Providers whose recon runs synchronously at upload time rather than via SFTP push — their
// audit rows go PENDING -> PROCESSING (with row counts) instead of the SFTP/END_POINT providers'
// PENDING -> PROCESSED. Manual trigger-recon has no SFTP config to run for these, so it is hidden
// for them; re-uploading is the real recovery. Mirrors the app-services SYNC_RECON_PROVIDERS
// string list (no cross-app import available).
export const UPLOAD_SOURCE_PROVIDERS: string[] = [
  SupportedReconProvider.FREIGHT_AIR_CANADA,
  SupportedReconProvider.POSTNL_UNIUNI,
  SupportedReconProvider.UBER,
];

// Retry failed rows (Uber only) mirrors the recon dedupe-key TTL: past this window the recovery
// path is manual assign per row, not Retry — see `isRetryVisible`.
export const RETRY_WINDOW_DAYS = 30;

// A pending/processing Uber row is "stuck" once its `updated_at` is this many hours old — the
// only other terminal-status Retry trigger before this constant existed was
// `processed_with_failures`, which left rows stranded by a deploy interruption, an unconfirmed
// enqueue, or the re-arm race with no recovery once re-upload is blocked by the duplicate check
// (BillID hard block). `updated_at` refreshes on every status write, including this same Retry's
// own flip to `processing`, so it doubles as the "already retried recently" guard — a second click
// inside the window still gets a 409 from app-services. Mirror of the app-services rule (no
// cross-app import available).
export const STUCK_ROW_STALE_HOURS = 2;
// How long after a run's last update its shipments may still be missing from the reporting mirror.
export const REPORTING_LAG_MINUTES = 60;

const count = (value: number): string => formatNumber(value, 'en-US');
const rowsWord = (value: number): string => (value === 1 ? 'row' : 'rows');

// `created_at` is the retry's own dedupe-key/tracker-TTL clock — unlike `updated_at`, it never
// moves on a later update, so it can't be nudged forward by a prior retry attempt.
export function isWithinRetryWindow(element: any): boolean {
  if (!element?.created_at) {
    return false;
  }
  const createdAt = DateTime.fromISO(element.created_at);
  if (!createdAt.isValid) {
    return false;
  }
  return DateTime.now().diff(createdAt, 'days').days <= RETRY_WINDOW_DAYS;
}

// A second Retry click within STUCK_ROW_STALE_HOURS gets no button, matching the 409
// app-services returns for it — the same flip to `processing` that a retry triggers also
// refreshes `updated_at`, so this check alone enforces both "old enough to be stuck" and "not
// already retried a moment ago".
export function isStaleUpdatedAt(element: any): boolean {
  if (!element?.updated_at) {
    return false;
  }
  const updatedAt = DateTime.fromISO(element.updated_at);
  if (!updatedAt.isValid) {
    return false;
  }
  return DateTime.now().diff(updatedAt, 'hours').hours >= STUCK_ROW_STALE_HOURS;
}

// Uber only, and only within the 30-day retry window app-services enforces server-side — a row
// outside the window (or missing `created_at`, e.g. a shape the retry route doesn't recognize)
// gets no button; the recovery there is manual assign per row. Two independent triggers share
// this one button: a processed_with_failures row with failed rows, or a pending/processing row
// stuck for more than STUCK_ROW_STALE_HOURS — a deploy interruption, an unconfirmed enqueue, or
// the re-arm race otherwise leave it stranded with no recovery once re-upload is blocked by the
// duplicate check.
export function isRetryVisible(element: any): boolean {
  if (element?.courier !== SupportedReconProvider.UBER || !isWithinRetryWindow(element)) {
    return false;
  }
  if (element.status === 'processed_with_failures') {
    return element.rows_failed > 0;
  }
  if (element.status === 'pending' || element.status === 'processing') {
    return isStaleUpdatedAt(element);
  }
  return false;
}

// The button/dialog label finance already knows from the duplicate-upload warning and the
// summary/failure Slack copy: "Retry failed rows" for a processed_with_failures row. A stuck
// pending/processing row has no failed-rows count to name (rows_failed may be NULL for it), so
// it gets the plainer "Retry" instead rather than a label that could be inaccurate.
export function getRetryButtonLabel(element: any): string {
  return element?.status === 'processed_with_failures' ? 'Retry failed rows' : 'Retry';
}

// Trigger-recon has no SFTP config to run for UPLOAD-source providers (it would flip the row
// to a terminal PROCESSING and then throw) — re-uploading is the real recovery for those, so
// the button only ever renders for a pending row belonging to a non-UPLOAD provider.
export function isTriggerReconVisible(element: any): boolean {
  return element?.status === 'pending' && !UPLOAD_SOURCE_PROVIDERS.includes(element.courier);
}

// Short status text: `processed_with_failures` is spelled out rather than shown as the raw enum.
export function getStatusCellText(element: any): string {
  return element?.status === 'processed_with_failures' ? 'processed with failures' : element?.status;
}

// The Courier Invoices table's status text, sentence case ("Processed with failures"). The detail
// page keeps the lowercase pill text from `getStatusCellText`.
export function getStatusLabel(element: any): string {
  const text = getStatusCellText(element);
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

// Rows column: the file's parsed row count. Only the UPLOAD couriers record counts; every other
// courier (and any row created before counts existed) shows a dash rather than a misleading 0.
export function getRowsCellText(element: any): string {
  return element?.rows_parsed == null ? '—' : count(element.rows_parsed);
}

export type RowsOutcomeTone = 'danger' | 'warning' | 'success';

// Behind the Rows column's info icon: what happened to the rows, one line per non-zero outcome.
// A row finalized before outcome counts existed only ever recorded its failed count.
export function getRowsOutcomeLines(element: any): string[] {
  if (element?.rows_done == null) {
    return element?.rows_failed > 0 ? [`${count(element.rows_failed)} Failed`] : [];
  }
  const outcomes: [number, string][] = [
    [element.rows_done, 'Reconciled'],
    [element.rows_review, 'Review'],
    [element.rows_failed, 'Failed'],
  ];
  return outcomes.filter(([value]) => value > 0).map(([value, label]) => `${count(value)} ${label}`);
}

// The Rows info icon's colour, worst outcome first: any failed row, then any row sent to review,
// then every row reconciled. Null when there is no outcome to show (same rows as no tooltip lines).
export function getRowsOutcomeTone(element: any): RowsOutcomeTone | null {
  if (element?.rows_failed > 0) {
    return 'danger';
  }
  if (element?.rows_done == null) {
    return null;
  }
  if (element.rows_review > 0) {
    return 'warning';
  }
  return element.rows_done > 0 ? 'success' : null;
}

// Failed-rows line for a processed_with_failures row. Re-uploading is blocked for Uber (the
// duplicate check catches the same BillID) — Retry is the one supported recovery. The "Retry
// within 30 days" suffix only applies to Uber (the only courier with Retry) and only while that
// window (created_at) is still open — past it, Retry is no longer offered (see `isRetryVisible`)
// and the badge shows just the failed count: naming a recovery path that doesn't exist for this
// row would mislead finance.
export function getFailedRowsBadgeText(element: any): string {
  if (element?.status !== 'processed_with_failures' || !(element.rows_failed > 0)) {
    return '';
  }
  const base = `${element.rows_failed} failed ${rowsWord(element.rows_failed)}`;
  const canRetry = element.courier === SupportedReconProvider.UBER && isWithinRetryWindow(element);
  return canRetry ? `${base} — Retry within 30 days` : base;
}

// A fully-deduplicated re-upload posts PROCESSED (UPLOAD providers finalize a 0-row run at
// once; older rows may still read PROCESSING) with the full rowsParsed count and zero
// rowsEnqueued, and is otherwise visually identical to a real run. Row counts are only ever
// recorded by the UPLOAD couriers, which enqueue one job per row, so "N skipped"
// (rowsParsed - rowsEnqueued) is a real row count for them.
export function getDuplicatesHintText(element: any): string {
  const isDuplicateStatus = element?.status === 'processing' || element?.status === 'processed';
  if (!isDuplicateStatus || element.rows_enqueued !== 0 || !(element.rows_parsed > 0)) {
    return '';
  }
  return `0 of ${count(element.rows_parsed)} rows enqueued — all rows were previously processed`;
}

// Everything behind the table's status info icon, one line per fact. Empty when there is nothing
// to add beyond the status itself.
export function getStatusTooltipLines(element: any): string[] {
  const lines: string[] = [];
  const duplicatesHint = getDuplicatesHintText(element);
  const hasCounts = element?.rows_parsed != null && element?.rows_enqueued != null;

  if (duplicatesHint) {
    lines.push(duplicatesHint);
  } else if (hasCounts) {
    lines.push(`${count(element.rows_enqueued)} of ${count(element.rows_parsed)} rows enqueued`);
    if (element.rows_done != null) {
      lines.push(
        `${count(element.rows_done)} reconciled, ${count(element.rows_review ?? 0)} sent to review, ` +
          `${count(element.rows_failed ?? 0)} failed`
      );
    }
    const skipped = element.rows_parsed - element.rows_enqueued;
    if (skipped > 0) {
      lines.push(`${count(skipped)} skipped — previously processed`);
    }
  }

  if (element?.status === 'processing' && !duplicatesHint) {
    lines.push('Reconciliation in progress — the status updates once every row has finished');
  }

  const failedBadge = getFailedRowsBadgeText(element);
  if (failedBadge) {
    lines.push(failedBadge);
  }

  if ((element?.status === 'pending' || element?.status === 'processing') && isRetryVisible(element)) {
    lines.push(`No progress for more than ${STUCK_ROW_STALE_HOURS} hours — use Retry`);
  }

  return lines;
}

// Column label for a failed row's identity, per courier.
export function getIdentifierLabel(courier: string): string {
  switch (courier) {
    case SupportedReconProvider.UBER:
      return 'Delivery Token';
    case SupportedReconProvider.FREIGHT_AIR_CANADA:
      return 'AWB';
    case SupportedReconProvider.POSTNL_UNIUNI:
      return 'CONNOTE';
    default:
      return 'Identifier';
  }
}

export function getSectionState(
  detail: Pick<CourierInvoiceAuditDetail, 'sections'> | null | undefined,
  section: InvoiceAuditSectionName
): InvoiceAuditSectionState {
  return detail?.sections?.[section] ?? 'ok';
}

const SECTION_NOUNS: Record<InvoiceAuditSectionName, string> = {
  shipments: 'reconciled shipments',
  unknownShipments: 'Unknown Tracking rows',
  failedRows: 'failed rows',
  siblings: 'other BillIDs',
};

// The notice shown in place of a section whose lookup failed — never that section's empty state.
// Null when the section loaded (or the payload predates per-section reporting).
export function getSectionNotice(
  detail: Pick<CourierInvoiceAuditDetail, 'sections'> | null | undefined,
  section: InvoiceAuditSectionName
): string | null {
  return getSectionState(detail, section) === 'unavailable'
    ? `Couldn't load ${SECTION_NOUNS[section]} — reload the page to try again.`
    : null;
}

// Rows the run actually reconciled (enqueued); the parsed count for a row that never recorded it.
function runRowCount(detail: CourierInvoiceAuditDetail): number | null {
  return detail.summary?.rowsEnqueued ?? detail.summary?.rowsParsed ?? null;
}

// The one-sentence answer to "what happened to this invoice's rows", first match wins.
export function buildOutcomeMessage(detail: CourierInvoiceAuditDetail): string {
  const { invoice, summary } = detail;
  if (detail.legacy) {
    return "Row-level outcomes aren't shown for bookmarked links — open this invoice from Courier Invoices to see them.";
  }
  if (!detail.trackingAvailable || !summary) {
    if (!UPLOAD_SOURCE_PROVIDERS.includes(invoice?.courier)) {
      return 'Row-level outcomes are not recorded for this courier.';
    }
    // An UPLOAD courier's row with no run recorded: still waiting, or from before counts existed.
    return invoice?.status === 'pending' || invoice?.status === 'processing'
      ? 'No reconciliation run has been recorded for this invoice yet.'
      : 'Row-level outcomes were not recorded for this invoice.';
  }
  if (invoice.status === 'pending' || invoice.status === 'processing') {
    return 'Reconciliation is running — counts update when every row finishes.';
  }
  const duplicatesHint = getDuplicatesHintText(invoice);
  if (duplicatesHint) {
    return duplicatesHint;
  }

  const reviewOpenKnown = getSectionState(detail, 'unknownShipments') === 'ok' && summary.reviewOpen != null;
  const failedLoggedKnown = getSectionState(detail, 'failedRows') === 'ok' && summary.failedLogged != null;

  if (summary.rowsDone == null) {
    const reviewPart = reviewOpenKnown
      ? `${count(summary.reviewOpen)} ${rowsWord(summary.reviewOpen)} ${summary.reviewOpen === 1 ? 'is' : 'are'} open in Unknown Tracking`
      : "Unknown Tracking rows couldn't be loaded";
    const failedPart = failedLoggedKnown
      ? `${count(summary.failedLogged)} failed ${rowsWord(summary.failedLogged)} ${summary.failedLogged === 1 ? 'is' : 'are'} logged`
      : "failed rows couldn't be loaded";
    return `Row counts were not recorded for this run (it finished before counts existed). ${reviewPart}; ${failedPart}.`;
  }

  const total = runRowCount(detail) ?? 0;
  const failed = summary.rowsFailed ?? 0;
  const review = summary.rowsReview ?? 0;
  const stillOpen = reviewOpenKnown ? ` (${count(summary.reviewOpen)} still open)` : '';

  if (failed > 0) {
    let next = '';
    if (invoice.courier === SupportedReconProvider.UBER) {
      next = isWithinRetryWindow(invoice)
        ? ' Use Retry failed rows.'
        : ' Retry window closed; assign or re-upload manually.';
    }
    return `${count(failed)} of ${count(total)} rows failed — see Failed rows.${next}`;
  }
  if (review > 0 && summary.rowsDone === 0) {
    return (
      `${count(review)} of ${count(total)} ${rowsWord(total)} could not be matched to a shipment ` +
      `and ${review === 1 ? 'was' : 'were'} sent to Unknown Tracking${stillOpen}.`
    );
  }
  const reviewClause = review > 0 ? `; ${count(review)} sent to Unknown Tracking${stillOpen}` : '';
  return `${count(summary.rowsDone)} of ${count(total)} ${rowsWord(total)} reconciled${reviewClause}.`;
}

// The Reconciled tab's empty state. Only meaningful when the shipments section loaded; callers
// show `getSectionNotice(detail, 'shipments')` instead when it did not.
export function buildReconciledEmptyState(detail: CourierInvoiceAuditDetail): string {
  const summary = detail.summary;
  const total = runRowCount(detail);
  if (detail.trackingAvailable && summary && total) {
    // Shipments are read from the reporting mirror, which can trail the run it just finalized. Past
    // that, reconciled rows can still link no shipment to this file (a PostNL row already
    // reconciled from an earlier file counts as done without being added to it).
    if (summary.rowsDone != null && summary.rowsDone > 0) {
      const rows = summary.rowsDone === 1 ? '1 row was' : `${count(summary.rowsDone)} rows were`;
      const updatedAt = DateTime.fromISO(detail.invoice?.updated_at ?? '');
      const recent = updatedAt.isValid && DateTime.now().diff(updatedAt, 'minutes').minutes < REPORTING_LAG_MINUTES;
      return recent
        ? `No shipments found yet — ${rows} reconciled, but reporting hasn't caught up. Reload in a few minutes.`
        : `No shipments found — ${rows} reconciled, but none of their shipments link to this invoice file.`;
    }
    if (summary.rowsReview === total) {
      return `No shipments found — all ${count(total)} rows went to Unknown Tracking`;
    }
    if (summary.rowsFailed === total) {
      return `No shipments found — all ${count(total)} rows failed`;
    }
  }
  return 'No shipments found for this invoice file';
}
